import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { MapPin, ArrowRight } from "lucide-react";

const CITIES = ["All cities", "New Delhi", "Mumbai", "Bengaluru", "Hyderabad", "Chennai", "Pune"];
const SPECIALTIES = ["All specialties", "Pulmonologist", "ENT", "Neurologist", "Sleep Medicine"];

export default function Doctors() {
  const [searchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [city, setCity] = useState(searchParams.get("city") || "All cities");
  const [specialty, setSpecialty] = useState("All specialties");
  const [loading, setLoading] = useState(true);
  const submissionId = searchParams.get("submissionId") || "";

  useEffect(() => {
    const params = {};
    if (city !== "All cities") params.city = city;
    if (specialty !== "All specialties") params.specialty = specialty;
    setLoading(true);
    api.get("/doctors", { params })
      .then(r => setDoctors(r.data.doctors))
      .finally(() => setLoading(false));
  }, [city, specialty]);

  return (
    <div data-testid="doctors-page">
      <Nav theme="light" />
      <div className="sv-directory">
        <div className="container-x">
          <span className="eyebrow eyebrow-navy">Find a specialist</span>
          <h1 data-testid="doctors-headline">Verified sleep specialists <span className="em-italic-navy">across India.</span></h1>
          <p style={{ fontSize: 17, color: "var(--ink-60)", maxWidth: 640, lineHeight: 1.55, marginTop: 16 }}>
            Every doctor listed has a verified NMC number and sleep-medicine training. Browse by city and specialty, then book a sleep study directly.
          </p>

          <div className="sv-filter-bar" data-testid="filter-bar">
            <select value={city} onChange={e => setCity(e.target.value)} data-testid="filter-city">
              {CITIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <select value={specialty} onChange={e => setSpecialty(e.target.value)} data-testid="filter-specialty">
              {SPECIALTIES.map(s => <option key={s}>{s}</option>)}
            </select>
            <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--ink-60)", padding: "10px 14px", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {loading ? "Searching..." : `${doctors.length} found`}
            </div>
          </div>

          <div className="sv-doc-grid">
            {doctors.map(d => (
              <div key={d.id} className="sv-doc-card" data-testid={`doctor-card-${d.slug}`}>
                <div className="sv-doc-head">
                  <div className="sv-doc-avatar">{d.avatar_initials}</div>
                  <div>
                    <h3>{d.name}</h3>
                    <div className="spec">{d.specialty}</div>
                    <div className="hosp"><MapPin size={12} style={{ display: "inline", marginRight: 4 }} />{d.hospital}, {d.city}</div>
                  </div>
                </div>
                <div className="badges">
                  <span className="badge">NMC Verified</span>
                  {d.telemedicine && <span className="badge">Telemedicine</span>}
                  {d.sponsored && <span className="badge sponsored">Sponsored</span>}
                  <span className="badge">{d.experience_years}+ yrs</span>
                </div>
                <div className="fee">From ₹{d.fee_in_person.toLocaleString()}</div>
                <div className="actions">
                  <Link to={`/doctors/${d.slug}`} className="btn btn-ghost" data-testid={`view-profile-${d.slug}`}>View profile</Link>
                  <Link to={`/book?doctor=${d.id}${submissionId ? `&submissionId=${submissionId}` : ""}`} className="btn btn-dark" data-testid={`book-doctor-${d.slug}`}>Book <ArrowRight size={14} /></Link>
                </div>
              </div>
            ))}
          </div>

          {!loading && doctors.length === 0 && (
            <div style={{ textAlign: "center", padding: 80, color: "var(--ink-60)" }} data-testid="no-doctors">
              No specialists match these filters yet. Try widening your search.
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
